import type { InventoryResource } from '@repo/api-gw-sdk';
import { BackupStatus } from '@repo/api-gw-sdk';

import { getActionStatus } from './resourceActionStatus';

export enum InventoryResourceActionsLevels {
  MainPrimary = 'MainPrimary',
  MainSecondary = 'MainSecondary',
  SubPrimary = 'SubPrimary',
  SubSecondary = 'SubSecondary',
  SubMore = 'SubMore',
}

export enum InventoryResourceActionsKeys {
  AssignToBackupPolicy = 'AssignToBackupPolicy',
  TakeSnapshot = 'TakeSnapshot',
  ExcludeFromBackup = 'ExcludeFromBackup',
  AllowBackups = 'AllowBackups',
  Explore = 'Explore',
  ShowActionRequired = 'ShowActionRequired',
  EnableResourceFromLimitExceeded = 'EnableResourceFromLimitExceeded',
  AttachRestoreTemplate = 'AttachRestoreTemplate',
  ManageSourceConnection = 'ManageSourceConnection',
}

// Names of the actions a status box offers, shared by the resource actions and the design catalog.
export const RESOURCE_ACTION_TITLES: Partial<
  Record<InventoryResourceActionsKeys, string>
> = {
  [InventoryResourceActionsKeys.AssignToBackupPolicy]:
    'Assign to Backup Policy',
  [InventoryResourceActionsKeys.AllowBackups]: 'Cancel Backup Exclusion',
  [InventoryResourceActionsKeys.ExcludeFromBackup]: 'Exclude from Backup',
  [InventoryResourceActionsKeys.ShowActionRequired]: 'Complete Configuration',
  [InventoryResourceActionsKeys.EnableResourceFromLimitExceeded]:
    'Enable Resource',
  [InventoryResourceActionsKeys.AttachRestoreTemplate]:
    'Attach Restore Template',
  [InventoryResourceActionsKeys.ManageSourceConnection]:
    'Manage Source Connection',
};

export type ActionsToLevel = Partial<
  Record<InventoryResourceActionsKeys, InventoryResourceActionsLevels>
>;
type BackupStatusToActionsMap = Record<BackupStatus, ActionsToLevel>;

export const backupStatusActions: BackupStatusToActionsMap = {
  [BackupStatus.Protected]: {
    [InventoryResourceActionsKeys.Explore]:
      InventoryResourceActionsLevels.MainPrimary,
    [InventoryResourceActionsKeys.TakeSnapshot]:
      InventoryResourceActionsLevels.MainSecondary,
    [InventoryResourceActionsKeys.AssignToBackupPolicy]:
      InventoryResourceActionsLevels.SubMore,
    [InventoryResourceActionsKeys.ExcludeFromBackup]:
      InventoryResourceActionsLevels.SubMore,
  },
  [BackupStatus.ViolationsDetected]: {
    [InventoryResourceActionsKeys.Explore]:
      InventoryResourceActionsLevels.MainPrimary,
    [InventoryResourceActionsKeys.TakeSnapshot]:
      InventoryResourceActionsLevels.MainSecondary,
    [InventoryResourceActionsKeys.AssignToBackupPolicy]:
      InventoryResourceActionsLevels.SubMore,
    [InventoryResourceActionsKeys.ExcludeFromBackup]:
      InventoryResourceActionsLevels.SubMore,
    // [InventoryResourceActionsKeys.MuteAllViolations]: InventoryResourceActionsLevels.SubMore,
  },
  [BackupStatus.NotBackedUp]: {
    [InventoryResourceActionsKeys.TakeSnapshot]:
      InventoryResourceActionsLevels.MainSecondary,
    [InventoryResourceActionsKeys.Explore]:
      InventoryResourceActionsLevels.MainPrimary,
    [InventoryResourceActionsKeys.AssignToBackupPolicy]:
      InventoryResourceActionsLevels.SubPrimary,
    [InventoryResourceActionsKeys.ExcludeFromBackup]:
      InventoryResourceActionsLevels.SubMore,
  },
  [BackupStatus.AllViolationsMuted]: {
    [InventoryResourceActionsKeys.Explore]:
      InventoryResourceActionsLevels.MainPrimary,
    [InventoryResourceActionsKeys.TakeSnapshot]:
      InventoryResourceActionsLevels.MainSecondary,
    [InventoryResourceActionsKeys.AssignToBackupPolicy]:
      InventoryResourceActionsLevels.SubMore,
    [InventoryResourceActionsKeys.ExcludeFromBackup]:
      InventoryResourceActionsLevels.SubMore,
    // [InventoryResourceActionsKeys.UnmuteAllViolations]: InventoryResourceActionsLevels.SubMore,
  },
  [BackupStatus.ExcludedFromBackup]: {
    [InventoryResourceActionsKeys.Explore]:
      InventoryResourceActionsLevels.MainPrimary,
    [InventoryResourceActionsKeys.TakeSnapshot]:
      InventoryResourceActionsLevels.MainSecondary,
    [InventoryResourceActionsKeys.AllowBackups]:
      InventoryResourceActionsLevels.SubPrimary,
  },
  [BackupStatus.GenericBackups]: {
    [InventoryResourceActionsKeys.Explore]:
      InventoryResourceActionsLevels.MainPrimary,
    [InventoryResourceActionsKeys.TakeSnapshot]:
      InventoryResourceActionsLevels.MainSecondary,
    [InventoryResourceActionsKeys.AssignToBackupPolicy]:
      InventoryResourceActionsLevels.SubPrimary,
    [InventoryResourceActionsKeys.ExcludeFromBackup]:
      InventoryResourceActionsLevels.SubMore,
  },
  [BackupStatus.InitialClassification]: {
    [InventoryResourceActionsKeys.ExcludeFromBackup]:
      InventoryResourceActionsLevels.SubMore,
  },
  [BackupStatus.Terminated]: {
    [InventoryResourceActionsKeys.TakeSnapshot]:
      InventoryResourceActionsLevels.MainSecondary,
    [InventoryResourceActionsKeys.Explore]:
      InventoryResourceActionsLevels.MainPrimary,
  },
  [BackupStatus.Disconnected]: {
    [InventoryResourceActionsKeys.TakeSnapshot]:
      InventoryResourceActionsLevels.MainSecondary,
    [InventoryResourceActionsKeys.Explore]:
      InventoryResourceActionsLevels.MainPrimary,
  },
  // No TakeSnapshot: the backup API rejects a resource whose source account Eon cannot
  // reach, so offering it would only surface that rejection as a failure.
  [BackupStatus.AccessLost]: {
    [InventoryResourceActionsKeys.Explore]:
      InventoryResourceActionsLevels.MainPrimary,
    [InventoryResourceActionsKeys.ManageSourceConnection]:
      InventoryResourceActionsLevels.SubPrimary,
  },
  [BackupStatus.BackupPostureStatusUnspecified]: {},
  [BackupStatus.Unsupported]: {},
  [BackupStatus.ClassificationFailed]: {
    [InventoryResourceActionsKeys.ExcludeFromBackup]:
      InventoryResourceActionsLevels.SubMore,
  },
  [BackupStatus.ActionRequired]: {
    [InventoryResourceActionsKeys.Explore]:
      InventoryResourceActionsLevels.MainPrimary,
    [InventoryResourceActionsKeys.TakeSnapshot]:
      InventoryResourceActionsLevels.MainSecondary,
    [InventoryResourceActionsKeys.ShowActionRequired]:
      InventoryResourceActionsLevels.SubPrimary,
    [InventoryResourceActionsKeys.ExcludeFromBackup]:
      InventoryResourceActionsLevels.SubMore,
  },
  // A storage gateway resource is covered by its gateway policy, so its status offers no actions of its own.
  [BackupStatus.Dlsg]: {
    [InventoryResourceActionsKeys.Explore]:
      InventoryResourceActionsLevels.MainPrimary,
    [InventoryResourceActionsKeys.TakeSnapshot]:
      InventoryResourceActionsLevels.MainSecondary,
  },
  [BackupStatus.LimitExceeded]: {
    [InventoryResourceActionsKeys.EnableResourceFromLimitExceeded]:
      InventoryResourceActionsLevels.SubMore,
  },
};

export const groupActionsByLevel = (
  resource: InventoryResource,
  actions: ResourceAction[],
  isStatusColumnsEnabled: boolean | undefined
): Partial<Record<InventoryResourceActionsLevels, ResourceAction[]>> => {
  const actionStatus = getActionStatus(resource, isStatusColumnsEnabled);
  const actionsByLevel = actions.reduce<
    Partial<Record<InventoryResourceActionsLevels, ResourceAction[]>>
  >((acc, action) => {
    const actionLevel: InventoryResourceActionsLevels | undefined =
      backupStatusActions[actionStatus][action.key];
    if (!actionLevel) {
      return acc;
    }

    if (!acc[actionLevel]) {
      acc[actionLevel] = [];
    }
    acc[actionLevel].push(action);
    return acc;
  }, {});

  return actionsByLevel;
};

export interface BaseAction {
  id: string;
  title?: string;
  tooltip: string;
  icon: string;
  execute: (resource: InventoryResource) => void;
  predicate?: () => boolean;
  disabled?: () => boolean;
  disabledTooltip?: string;
  isAuthorized?: boolean;
}

export interface ResourceAction extends BaseAction {
  key: InventoryResourceActionsKeys;
}
